/**
 * Svelte 5 Error Handling Hook
 * Provides centralized error management for components
 */

import { onDestroy } from 'svelte';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface ErrorContext {
  component?: string;
  action?: string;
  timestamp?: number;
  metadata?: Record<string, any>;
}

export interface ErrorState {
  message: string | null;
  severity: ErrorSeverity;
  context?: ErrorContext;
  isVisible: boolean;
}

export interface ErrorOptions {
  duration?: number; // Auto-dismiss duration in ms
  severity?: ErrorSeverity;
  showToUser?: boolean; // Whether to show error to user
  logToConsole?: boolean; // Whether to log to console
  context?: ErrorContext;
}

/**
 * Creates an error handling hook for Svelte 5 components
 * Must be called during component initialization
 */
export function useError(componentName?: string) {
  // Track active timers for cleanup
  let dismissTimer: ReturnType<typeof setTimeout> | null = null;
  
  // Cleanup on component destroy
  onDestroy(() => {
    if (dismissTimer) {
      clearTimeout(dismissTimer);
    }
  });
  
  /**
   * Handle error with options
   */
  function handleError(
    error: unknown, 
    options: ErrorOptions = {}
  ): ErrorState {
    const {
      duration = 5000,
      severity = 'error',
      showToUser = true,
      logToConsole = true,
      context = {}
    } = options;
    
    // Extract error message
    const message = extractErrorMessage(error);
    
    // Create error state
    const errorState: ErrorState = {
      message,
      severity,
      context: {
        ...context,
        component: componentName || context.component,
        timestamp: Date.now()
      },
      isVisible: showToUser
    };
    
    // Log to console if enabled
    if (logToConsole) {
      logError(errorState);
    }
    
    // Auto-dismiss if duration is set
    if (duration > 0 && showToUser) {
      if (dismissTimer) {
        clearTimeout(dismissTimer);
      }
      
      dismissTimer = setTimeout(() => {
        errorState.isVisible = false;
        dismissTimer = null;
      }, duration);
    }
    
    return errorState;
  }
  
  /**
   * Clear current error
   */
  function clearError() {
    if (dismissTimer) {
      clearTimeout(dismissTimer);
      dismissTimer = null;
    }
  }
  
  /**
   * Wrap async function with error handling
   */
  function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    options?: ErrorOptions
  ): T {
    return (async (...args: Parameters<T>) => {
      try {
        return await fn(...args);
      } catch (error) {
        const errorState = handleError(error, options);
        throw new ErrorWithState(errorState, error);
      }
    }) as T;
  }
  
  /**
   * Execute function with error boundary
   */
  async function tryExecute<T>(
    fn: () => T | Promise<T>,
    options?: ErrorOptions
  ): Promise<[T | null, ErrorState | null]> {
    try {
      const result = await fn();
      return [result, null];
    } catch (error) {
      const errorState = handleError(error, options);
      return [null, errorState];
    }
  }
  
  return {
    handleError,
    clearError,
    withErrorHandling,
    tryExecute
  };
}

/**
 * Extract error message from various error types
 */
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  
  if (error && typeof error === 'object' && 'error' in error) {
    return extractErrorMessage(error.error);
  }
  
  return '알 수 없는 오류가 발생했습니다';
}

/**
 * Log error based on severity
 */
function logError(errorState: ErrorState) {
  const prefix = `[${errorState.severity.toUpperCase()}]`;
  const component = errorState.context?.component ? ` [${errorState.context.component}]` : '';
  const message = `${prefix}${component} ${errorState.message}`;
  
  switch (errorState.severity) {
    case 'info':
      console.info(message, errorState.context);
      break;
    case 'warning':
      console.warn(message, errorState.context);
      break;
    case 'error':
    case 'critical':
      console.error(message, errorState.context);
      break;
  }
}

/**
 * Custom error class with state
 */
class ErrorWithState extends Error {
  constructor(
    public errorState: ErrorState,
    public originalError: unknown
  ) {
    super(errorState.message || 'An error occurred');
    this.name = 'ErrorWithState';
  }
}

/**
 * Type guard for ErrorWithState
 */
export function isErrorWithState(error: unknown): error is ErrorWithState {
  return error instanceof ErrorWithState;
}

/**
 * Create a type-safe error handler for specific error types
 */
export function createTypedErrorHandler<T extends Error>(
  errorType: new (...args: any[]) => T,
  handler: (error: T) => ErrorOptions | void
) {
  return (error: unknown): ErrorOptions | null => {
    if (error instanceof errorType) {
      const options = handler(error);
      return options || null;
    }
    return null;
  };
}